import React, { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Card } from "../ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../ui/dialog";
import { Label } from "../ui/label";
import { toast } from "sonner";
import { Plus, Trash, Edit, ArrowLeft } from "lucide-react";
import { processService } from "../../services/process.service";

const { createProcesso, deleteProcesso, updateProcesso } = processService;

export function AdminProcessList({ processos, loading, onSelectProcess, onRefresh, onBack }) {
  const [isNewProcessoDialogOpen, setIsNewProcessoDialogOpen] = useState(false);
  const [newProcessoData, setNewProcessoData] = useState({ nome_processo: "", cnj: "", vara: "", uf: "", fluxo: "principal" });

  const [isEditProcessoDialogOpen, setIsEditProcessoDialogOpen] = useState(false);
  const [editingProcesso, setEditingProcesso] = useState(null);
  const [editProcessoData, setEditProcessoData] = useState({ nome_processo: "", cnj: "", vara: "", uf: "", fluxo: "principal" });

  const handleCreateProcesso = async () => {
    try {
      await createProcesso(newProcessoData);
      toast.success("Processo criado com sucesso");
      setIsNewProcessoDialogOpen(false);
      setNewProcessoData({ nome_processo: "", cnj: "", vara: "", uf: "", fluxo: "principal" });
      onRefresh();
    } catch (e) {
      toast.error("Erro ao criar processo: " + e.message);
    }
  };

  const handleUpdateProcesso = async () => {
    try {
      await updateProcesso(editingProcesso.id, editProcessoData);
      toast.success("Processo atualizado com sucesso");
      setIsEditProcessoDialogOpen(false);
      onRefresh();
    } catch (e) {
      toast.error("Erro ao atualizar processo: " + e.message);
    }
  };

  const handleOpenEditDialog = (processo) => {
    setEditingProcesso(processo);
    setEditProcessoData({
      nome_processo: processo.nome_processo || "",
      cnj: processo.cnj || "",
      vara: processo.vara || "",
      uf: processo.uf || "",
      fluxo: processo.fluxo || "principal"
    });
    setIsEditProcessoDialogOpen(true);
  };

  const handleDeleteProcesso = async (id) => {
    if (!window.confirm("Tem certeza? Isso apagará todos os documentos e assinantes vinculados.")) return;
    try {
      await deleteProcesso(id);
      toast.success("Processo removido");
      onRefresh();
    } catch (e) {
      toast.error("Erro ao remover processo");
    }
  };

  if (loading) return <div className="p-8 text-center">Carregando processos...</div>;

  return (
    <Card className="p-8 md:p-10 shadow-lg border-0 bg-white/95">
      <div className="flex justify-between items-center mb-8">
        <div className="flex items-center gap-4">
          {onBack && (
            <Button variant="outline" size="icon" onClick={onBack}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
          )}
          <h2 className="text-2xl font-semibold text-slate-800">
            Gerenciamento de Processos
          </h2>
        </div>
        <div className="flex gap-2">
          <Dialog open={isNewProcessoDialogOpen} onOpenChange={setIsNewProcessoDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700">
                <Plus className="mr-2 h-4 w-4" /> Novo Processo
              </Button>
            </DialogTrigger>
            <DialogContent>
            <DialogHeader>
              <DialogTitle>Novo Processo</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Nome do Processo</Label>
                <Input
                  value={newProcessoData.nome_processo}
                  onChange={e => setNewProcessoData({ ...newProcessoData, nome_processo: e.target.value })}
                  placeholder="Ex: Equatorial 2024"
                />
              </div>
              <div className="space-y-2">
                <Label>CNJ</Label>
                <Input
                  value={newProcessoData.cnj}
                  onChange={e => setNewProcessoData({ ...newProcessoData, cnj: e.target.value })}
                  placeholder="0000000-00.0000.0.00.0000"
                />
              </div>
              <div className="space-y-2">
                <Label>Vara</Label>
                <Input
                  value={newProcessoData.vara}
                  onChange={e => setNewProcessoData({ ...newProcessoData, vara: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>UF</Label>
                <Input
                  value={newProcessoData.uf}
                  onChange={e => setNewProcessoData({ ...newProcessoData, uf: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Fluxo</Label>
                <select
                  className="w-full border rounded px-3 py-2 text-sm"
                  value={newProcessoData.fluxo}
                  onChange={e => setNewProcessoData({ ...newProcessoData, fluxo: e.target.value })}
                >
                  <option value="principal">Principal</option>
                  <option value="customizado">Customizado</option>
                </select>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={handleCreateProcesso}>Criar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <Table>
        <TableHeader className="bg-slate-50">
          <TableRow>
            <TableHead className="py-4">Nome</TableHead>
            <TableHead className="py-4">CNJ</TableHead>
            <TableHead className="py-4">Vara</TableHead>
            <TableHead className="py-4">UF</TableHead>
            <TableHead className="py-4 text-right">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {processos.map((processo) => (
            <TableRow key={processo.id} className="hover:bg-slate-50">
              <TableCell className="font-medium">{processo.nome_processo}</TableCell>
              <TableCell className="font-mono text-xs">{processo.cnj || "-"}</TableCell>
              <TableCell>{processo.vara}</TableCell>
              <TableCell>{processo.uf}</TableCell>
              <TableCell className="text-right flex justify-end gap-1">
                <Button variant="outline" size="sm" onClick={() => handleOpenEditDialog(processo)}>
                  <Edit className="h-4 w-4 mr-1" /> Editar
                </Button>
                <Button variant="outline" size="sm" onClick={() => onSelectProcess(processo)}>
                  Gerenciar
                </Button>
                <Button variant="ghost" size="sm" onClick={() => handleDeleteProcesso(processo.id)} className="h-8 w-8 p-0">
                  <Trash className="h-4 w-4 text-red-500" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
          {processos.length === 0 && (
            <TableRow>
              <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                Nenhum processo cadastrado.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <Dialog open={isEditProcessoDialogOpen} onOpenChange={setIsEditProcessoDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Processo</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nome do Processo</Label>
              <Input
                value={editProcessoData.nome_processo}
                onChange={e => setEditProcessoData({ ...editProcessoData, nome_processo: e.target.value })}
                placeholder="Ex: Equatorial 2024"
              />
            </div>
            <div className="space-y-2">
              <Label>CNJ</Label>
              <Input
                value={editProcessoData.cnj}
                onChange={e => setEditProcessoData({ ...editProcessoData, cnj: e.target.value })}
                placeholder="0000000-00.0000.0.00.0000"
              />
            </div>
            <div className="space-y-2">
              <Label>Vara</Label>
              <Input
                value={editProcessoData.vara}
                onChange={e => setEditProcessoData({ ...editProcessoData, vara: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>UF</Label>
              <Input
                value={editProcessoData.uf}
                onChange={e => setEditProcessoData({ ...editProcessoData, uf: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Fluxo</Label>
              <select
                className="w-full border rounded px-3 py-2 text-sm"
                value={editProcessoData.fluxo}
                onChange={e => setEditProcessoData({ ...editProcessoData, fluxo: e.target.value })}
              >
                <option value="principal">Principal</option>
                <option value="customizado">Customizado</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditProcessoDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleUpdateProcesso}>Salvar Alterações</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
